import { ReactElement, useCallback } from "react";

import {
  ActionKitIntegration,
  ImportCampaignContactsFromActionKitMutation,
  ImportCampaignContactsFromActionKitMutationVariables,
  CampaignContactActionKitImportArgs,
} from "~frontend/__generated__/graphql";
import { ACTION_KIT_CAMPAIGN_CONTACT_IMPORT } from "~frontend/graphql/campaigns";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";
import ImportForm, {
  ActionKitFormOutput,
  ActionKitFormToApiTransformer,
} from "./ImportForm";

export default function ActionKitImport({
  campaign,
  availableActionKitIntegrations,
  readOnly,
  previousJobArgs,
  displayErrorToast,
  resetContactSourceSettings,
}: {
  campaign: CampaignForEditor;
  availableActionKitIntegrations: ActionKitIntegration[];
  readOnly?: boolean;
  previousJobArgs?: CampaignContactActionKitImportArgs;
  displayErrorToast: (message: string, errors: unknown) => void;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const vanIntegrationEnabled = !!(
    campaign.integration && campaign.integration.service == "NGPVAN"
  );
  const enableMultiPhoneNumberInput = previousJobArgs
    ? previousJobArgs.phoneNumberColumns.length > 1
    : false;
  const phoneNumberColumns = previousJobArgs?.phoneNumberColumns
    ? previousJobArgs.phoneNumberColumns.join(", ")
    : "";

  const [startImport] = useMutation<
    ImportCampaignContactsFromActionKitMutation,
    ImportCampaignContactsFromActionKitMutationVariables
  >(ACTION_KIT_CAMPAIGN_CONTACT_IMPORT);

  const startActionKitImport = useCallback(
    async (formInput: ActionKitFormOutput) => {
      const input = ActionKitFormToApiTransformer.parse(formInput);
      const { errors } = await startImport({
        variables: {
          input,
        },
      });

      if (errors) {
        displayErrorToast("Failed to start ActionKit import", errors);
      }
    },
    [startImport, displayErrorToast]
  );

  return (
    <ImportForm
      onSubmit={startActionKitImport}
      availableActionKitIntegrations={availableActionKitIntegrations}
      initialValues={{
        campaignId: campaign.id,
        integrationId:
          previousJobArgs?.integrationId ??
          availableActionKitIntegrations[0]?.id,
        phoneNumberColumns,
        vanIntegrationEnabled,
        enableMultiPhoneNumberInput,
        reportShortName: previousJobArgs?.reportShortName ?? "",
        vanIdColumn: previousJobArgs?.vanIdColumn ?? "",
      }}
      readOnly={readOnly}
      resetContactSourceSettings={resetContactSourceSettings}
    />
  );
}
